import { DragEscapeGuard } from 'src/modules/shared/components/atoms/drag-escape-guard';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { PropertyMediaKind, FileStatus, type TCFile } from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import { MediaPreviewCard } from 'src/modules/shared/components/molecules/media-preview-card';
import { MediaUploadDropzone } from 'src/modules/shared/components/molecules/media-upload-dropzone';
import { DraggableItem } from 'src/modules/shared/components/molecules/draggable-item';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { uploadFileWithUploadSession } from 'src/modules/shared/hooks/api/files-upload-session';
import { useFilesMetadata } from 'src/modules/shared/hooks/api/files';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import {
  getPropertyMediaAcceptValue,
  inferPropertyMediaKind,
  type SupportedPropertyMediaKind,
} from 'src/modules/shared/utils/file/media-kind.util';
import {
  getPropertyMediaCountLabel,
  getPropertyMediaFileIds,
  normalizePropertyMediaPositions,
  reorderPropertyMedia,
} from '../../utils/property-media.util';

const PROPERTY_MEDIA_FILE_TAGS = {
  source: 'catalog',
  kind: 'property-media',
} as const;

const isReadyFile = (file: TCFile | null): file is TCFile =>
  file != null && file.status === FileStatus.READY;

interface PropertyMediaManagerProps {
  /** Ordered property-media collection currently attached to the property. */
  media: PropertyMediaInputLike[];
  /** Called when the property-media collection changes. */
  onChange?: (nextMedia: PropertyMediaFormValue[]) => void;
  /** Selected file ids when the manager is used in selection mode. */
  selectedFileIds?: string[];
  /** Called when the selected file ids change. */
  onSelectedFileIdsChange?: (nextFileIds: string[]) => void;
  /** Owning property id used to tag uploaded files. */
  propertyId?: number | null;
  /** Whether uploads/removals/reordering should be enabled. */
  editable?: boolean;
  /** Whether user actions should be disabled. */
  disabled?: boolean;
  /** Section title displayed above the manager. */
  title: string;
  /** Section description displayed above the manager. */
  description: string;
  /** Empty-state label displayed when there is no visible media. */
  emptyLabel: string;
  /** Optional hint displayed in selection mode. */
  selectionHint?: string;
  /** Optional media-kind filter for the rendered collection. */
  allowedKinds?: SupportedPropertyMediaKind[];
}

interface PropertyMediaFormValue {
  /** Files service identifier backing this media item. */
  fileId: string;
  /** Stored property-media kind. */
  kind?: SupportedPropertyMediaKind;
  /** Zero-based display order. */
  position?: number;
  /** Optional source URL used during import. */
  sourceUrl?: string | null;
  /** Optional source-system identifier. */
  sourceId?: string | null;
  /** Optional source system label. */
  sourceSystem?: string | null;
  /** Optional user-facing media title. */
  title?: string | null;
  /** Optional accessible alt text. */
  altText?: string | null;
}

interface PropertyMediaInputLike {
  /** Files service identifier backing this media item. */
  fileId?: string;
  /** Stored property-media kind. */
  kind?: SupportedPropertyMediaKind;
  /** Zero-based display order. */
  position?: number;
  /** Optional source URL used during import. */
  sourceUrl?: string | null;
  /** Optional source-system identifier. */
  sourceId?: string | null;
  /** Optional source system label. */
  sourceSystem?: string | null;
  /** Optional user-facing media title. */
  title?: string | null;
  /** Optional accessible alt text. */
  altText?: string | null;
}

const toPropertyMediaFormValue = (
  mediaItem: PropertyMediaInputLike
): PropertyMediaFormValue | null => {
  const fileId = mediaItem.fileId?.trim();

  if (!fileId) {
    return null;
  }

  return {
    fileId,
    kind: mediaItem.kind,
    position: mediaItem.position,
    sourceUrl: mediaItem.sourceUrl,
    sourceId: mediaItem.sourceId,
    sourceSystem: mediaItem.sourceSystem,
    title: mediaItem.title,
    altText: mediaItem.altText,
  };
};

/**
 * Property-level media manager backed by the Files service.
 */
export const PropertyMediaManager: React.FC<PropertyMediaManagerProps> = ({
  media,
  onChange,
  selectedFileIds,
  onSelectedFileIdsChange,
  propertyId,
  editable = true,
  disabled = false,
  title,
  description,
  emptyLabel,
  selectionHint,
  allowedKinds,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const editableEnabled = editable && Boolean(onChange);
  const normalizedMedia = useMemo(
    () =>
      media
        .map(toPropertyMediaFormValue)
        .filter(
          (mediaItem): mediaItem is PropertyMediaFormValue => mediaItem != null
        ),
    [media]
  );

  const visibleMedia = useMemo(
    () =>
      allowedKinds?.length
        ? normalizedMedia.filter((item) =>
            allowedKinds.includes(item.kind ?? PropertyMediaKind.IMAGE)
          )
        : normalizedMedia,
    [allowedKinds, normalizedMedia]
  );
  const fileIds = useMemo(
    () => getPropertyMediaFileIds(visibleMedia),
    [visibleMedia]
  );
  const {
    files: resolvedFiles,
    loading: loadingFiles,
    error: filesError,
  } = useFilesMetadata(fileIds);
  const selectionEnabled =
    !editableEnabled && Boolean(onSelectedFileIdsChange) && fileIds.length > 0;

  useEffect(() => {
    if (filesError) {
      showAlert(extractApiErrorMessage(filesError), 'error');
    }
  }, [filesError, showAlert]);

  const files = useMemo(() => {
    const readyFiles = resolvedFiles.filter(isReadyFile);
    return fileIds
      .map((fileId) => readyFiles.find((file) => file.id === fileId) ?? null)
      .filter(isReadyFile);
  }, [fileIds, resolvedFiles]);

  const countLabel = useMemo(
    () => getPropertyMediaCountLabel(visibleMedia.length, t),
    [visibleMedia.length, t]
  );

  const fallbackKindByFileId = useMemo(
    () =>
      new Map(
        visibleMedia.map((item) => [
          item.fileId,
          item.kind ?? PropertyMediaKind.IMAGE,
        ])
      ),
    [visibleMedia]
  );

  const handleRemove = (fileId: string) => {
    if (!onChange) {
      return;
    }

    onChange(
      normalizePropertyMediaPositions(
        normalizedMedia.filter(
          (currentMediaItem) => currentMediaItem.fileId !== fileId
        )
      )
    );
  };

  const handleToggleSelection = (fileId: string) => {
    if (!onSelectedFileIdsChange) {
      return;
    }

    const currentSelection = selectedFileIds ?? [];
    const nextSelection = currentSelection.includes(fileId)
      ? currentSelection.filter((currentFileId) => currentFileId !== fileId)
      : [...currentSelection, fileId];

    onSelectedFileIdsChange(nextSelection);
  };

  const uploadFiles = async (selectedFiles: File[]) => {
    if (!onChange || selectedFiles.length === 0) {
      return;
    }

    setUploadingFiles(true);

    try {
      const uploadedFiles = await Promise.all(
        selectedFiles.map(async (file) => {
          return await uploadFileWithUploadSession({
            file,
            tags: {
              ...PROPERTY_MEDIA_FILE_TAGS,
              ...(propertyId ? { propertyId: String(propertyId) } : {}),
            },
          });
        })
      );

      const uploadedMedia = uploadedFiles.reduce<PropertyMediaFormValue[]>(
        (items: PropertyMediaFormValue[], file: TCFile, index: number) => {
          const fileId = file.id;

          if (!fileId) {
            return items;
          }

          items.push({
            fileId,
            kind: inferPropertyMediaKind(
              file.contentType,
              PropertyMediaKind.DOCUMENT
            ),
            position: normalizedMedia.length + index,
          });

          return items;
        },
        []
      );

      const nextMedia = normalizePropertyMediaPositions([
        ...normalizedMedia,
        ...uploadedMedia,
      ]);

      onChange(nextMedia);
    } catch (error) {
      showAlert(extractApiErrorMessage(error), 'error');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = '';
    await uploadFiles(selectedFiles);
  };

  const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (event) => {
    if (!editableEnabled || disabled) {
      return;
    }

    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (event) => {
    if (!editableEnabled || disabled) {
      return;
    }

    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (event) => {
    if (!editableEnabled || disabled) {
      return;
    }

    event.preventDefault();
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (event) => {
    if (!editableEnabled || disabled) {
      return;
    }

    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    void uploadFiles(droppedFiles);
  };

  const handleBrowse = () => {
    if (disabled) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleSortStart = (event: DragStartEvent) => {
    setActiveFileId(String(event.active.id));
  };

  const handleSortEnd = (event: DragEndEvent) => {
    setActiveFileId(null);

    if (!onChange || event.over == null || event.active.id === event.over.id) {
      return;
    }

    const orderedFileIds = files
      .map((file) => file.id)
      .filter((fileId): fileId is string => Boolean(fileId));
    const previousIndex = orderedFileIds.findIndex(
      (fileId) => fileId === event.active.id
    );
    const nextIndex = orderedFileIds.findIndex(
      (fileId) => fileId === event.over?.id
    );

    if (previousIndex < 0 || nextIndex < 0) {
      return;
    }

    const reorderedVisibleMedia = reorderPropertyMedia(
      visibleMedia,
      previousIndex,
      nextIndex
    );
    const reorderedFileIds = arrayMove(
      orderedFileIds,
      previousIndex,
      nextIndex
    );
    const reorderedVisibleMediaById = new Map(
      reorderedVisibleMedia.map((item) => [item.fileId, item] as const)
    );

    onChange(
      normalizePropertyMediaPositions([
        ...normalizedMedia.filter(
          (item) => !reorderedVisibleMediaById.has(item.fileId)
        ),
        ...reorderedFileIds.reduce<PropertyMediaFormValue[]>(
          (items, fileId) => {
            const mediaItem = reorderedVisibleMediaById.get(fileId);

            if (mediaItem) {
              items.push(mediaItem);
            }

            return items;
          },
          []
        ),
      ])
    );
  };

  const activeFile = files.find((file) => file.id === activeFileId) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Text size="small" weight="medium" className="text-slate-900">
          {title}
        </Text>
        <Text size="tiny" className="text-slate-500">
          {description}
        </Text>
      </div>

      {editableEnabled ? (
        <div>
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            multiple
            accept={getPropertyMediaAcceptValue()}
            className="sr-only"
            onChange={handleUpload}
          />
          <MediaUploadDropzone
            editable={editableEnabled}
            disabled={disabled || uploadingFiles}
            loading={uploadingFiles}
            dragging={isDragging}
            countLabel={countLabel}
            title={t('catalog.properties.media.dropzone.title')}
            helper={t('catalog.properties.media.dropzone.helper')}
            actionLabel={t('catalog.properties.media.upload')}
            emptyLabel={emptyLabel}
            onBrowse={handleBrowse}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />
        </div>
      ) : null}

      {selectionHint ? (
        <Text size="tiny" className="text-slate-500">
          {selectionHint}
        </Text>
      ) : null}

      {loadingFiles ? (
        <div className="max-h-[28rem] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: Math.max(visibleMedia.length || 1, 3) }).map(
              (_, index) => (
                <TextSkeleton
                  key={`media-skeleton-${index}`}
                  className="h-56 w-full rounded-2xl"
                />
              )
            )}
          </div>
        </div>
      ) : files.length > 0 ? (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleSortEnd}
          onDragStart={handleSortStart}
          onDragCancel={() => setActiveFileId(null)}
          sensors={editableEnabled ? sensors : undefined}
        >
          <DragEscapeGuard />
          <SortableContext
            items={files.map((file) => file.id)}
            strategy={rectSortingStrategy}
          >
            <div className="max-h-[28rem] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {files.map((file, index) => {
                  const fileId = file.id;
                  const fallbackKind = fallbackKindByFileId.get(fileId);
                  const isSelected = (selectedFileIds ?? []).includes(fileId);

                  return (
                    <DraggableItem
                      key={fileId}
                      id={fileId}
                      data={{ fileId }}
                      disabled={!editableEnabled}
                      unstyled
                      className="cursor-default"
                    >
                      {({ DragHandle }) => (
                        <MediaPreviewCard
                          file={file}
                          index={index}
                          editable={editableEnabled}
                          selectable={selectionEnabled}
                          selected={isSelected}
                          fallbackKind={fallbackKind}
                          previewLabel={t('catalog.properties.media.preview')}
                          removeLabel={t('catalog.properties.media.remove')}
                          selectLabel={t('catalog.properties.media.select')}
                          selectedLabel={t('catalog.properties.media.selected')}
                          dragHandleLabel={t(
                            'catalog.properties.media.reorder'
                          )}
                          DragHandle={editableEnabled ? DragHandle : undefined}
                          onToggleSelection={handleToggleSelection}
                          onRemove={handleRemove}
                        />
                      )}
                    </DraggableItem>
                  );
                })}
              </div>
            </div>
          </SortableContext>

          {activeFile ? (
            <DragOverlay dropAnimation={null}>
              <div className="w-full max-w-sm opacity-90">
                <MediaPreviewCard
                  file={activeFile}
                  index={
                    files.findIndex((file) => file.id === activeFile.id) >= 0
                      ? files.findIndex((file) => file.id === activeFile.id)
                      : 0
                  }
                  editable={editableEnabled}
                  fallbackKind={fallbackKindByFileId.get(activeFile.id)}
                  previewLabel={t('catalog.properties.media.preview')}
                  removeLabel={t('catalog.properties.media.remove')}
                  selectLabel={t('catalog.properties.media.select')}
                  selectedLabel={t('catalog.properties.media.selected')}
                  dragHandleLabel={t('catalog.properties.media.reorder')}
                />
              </div>
            </DragOverlay>
          ) : null}
        </DndContext>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
          <Text size="small" className="text-slate-500">
            {emptyLabel}
          </Text>
        </div>
      )}
    </div>
  );
};
